import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";
import {
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import PauseIcon from "@mui/icons-material/Pause";
import MicOffIcon from "@mui/icons-material/MicOff";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DialpadIcon from "@mui/icons-material/Dialpad";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import DialerSipIcon from "@mui/icons-material/DialerSip";
import LinkIcon from "@mui/icons-material/Link";
import CallEndIcon from "@mui/icons-material/CallEnd";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100vh",
    maxWidth: "100vw",
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minHeight: "60px",
    borderRadius: "0 0 10px 10px",
    backgroundColor: theme.palette.primary.light,
    [theme.breakpoints.up("md")]: {
      minHeight: "90px",
    },
  },
  title: {
    flex: "auto",
    color: "white",
    fontWeight: 300,
  },
  content: {
    overflowY: "auto",
    padding: theme.spacing(3, 3, 5),
    backgroundColor: "#f3f5f8",
    flex: 1,
    maxHeight: `calc(100vh - ${48 + 60}px)`,
    [theme.breakpoints.up("md")]: {
      maxHeight: `calc(100vh - ${64 + 90}px)`,
    },
  },
  // One readable column, centred on wide screens.
  column: {
    maxWidth: 980,
    margin: "0 auto",
  },
  intro: {
    fontSize: 15,
    lineHeight: 1.55,
    color: theme.palette.text.secondary,
    maxWidth: 720,
  },
  section: {
    marginTop: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  sectionHint: {
    fontSize: 13.5,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  grid: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "1fr",
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  card: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.75),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  cardIcon: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    backgroundColor: "rgba(1, 186, 239, 0.13)",
    "& svg": {
      fontSize: 21,
    },
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  cardText: {
    marginTop: 2,
    fontSize: 13.5,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  note: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2, 2.5),
    borderRadius: 12,
    border: "1px solid rgba(1, 186, 239, 0.35)",
    backgroundColor: "rgba(1, 186, 239, 0.07)",
  },
  noteTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.text.primary,
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  noteList: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2.5),
    fontSize: 13.5,
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    "& li + li": {
      marginTop: 4,
    },
  },
}));

const callFeatures = [
  {
    icon: <CallIcon />,
    title: "Place a call",
    text: "Dial a number on the keypad or pick a contact from the phonebook, then press the green call button. You hear a ringback tone until the other side answers.",
  },
  {
    icon: <CallReceivedIcon />,
    title: "Receive a call",
    text: "Incoming calls ring in the app and open the phone screen automatically. The caller is matched against your phonebook; otherwise the number and the name sent by the caller are shown.",
  },
  {
    icon: <CallEndIcon />,
    title: "Answer and hang up",
    text: "Each call has its own answer and hang-up controls. If the other party hangs up, the call is cleared automatically.",
  },
  {
    icon: <CallSplitIcon />,
    title: "Two calls at once",
    text: "You can have two calls at the same time. Placing or answering a second call puts the first one on hold. A third incoming call is rejected as busy.",
  },
  {
    icon: <PauseIcon />,
    title: "Hold and resume",
    text: "Put a call on hold and resume it later. Resuming one call automatically holds the other one.",
  },
  {
    icon: <MicOffIcon />,
    title: "Mute",
    text: "Mute your microphone for a call. While in a conference, mute applies to all participants at once.",
  },
  {
    icon: <DialpadIcon />,
    title: "Keypad during a call",
    text: "Use the keypad while connected to send digits, for example to navigate a menu or enter a PIN. In a conference the digits go to every participant.",
  },
  {
    icon: <PhoneForwardedIcon />,
    title: "Transfer a call",
    text: "Transfer the active call to another extension or number. The transfer is performed by the phone system, so it is available only where the system supports it.",
  },
  {
    icon: <FiberManualRecordIcon />,
    title: "Record a call",
    text: "Start and stop recording the active call. Recording is performed by the phone system and the result appears under Call Recordings.",
  },
  {
    icon: <CallMergeIcon />,
    title: "Conference",
    text: "With two calls in progress, merge them into a three-way conference. Audio is mixed in your browser. Hanging up ends both legs of the conference.",
  },
];

const generalFeatures = [
  {
    icon: <DialerSipIcon />,
    title: "Registration status",
    text: "The phone icon in the top bar shows whether the app is registered with the phone system. Calls are possible only while registered.",
  },
  {
    icon: <LinkIcon />,
    title: "Click to dial",
    text: "Phone links on web pages (tel: links) open the app with the number filled in. Automatic dialing can be switched on in the app settings.",
  },
];

const renderFeatures = (classes, features) => (
  <div className={classes.grid}>
    {features.map((feature) => (
      <div key={feature.title} className={classes.card}>
        <span className={classes.cardIcon}>{feature.icon}</span>
        <div>
          <div className={classes.cardTitle}>{feature.title}</div>
          <div className={classes.cardText}>{feature.text}</div>
        </div>
      </div>
    ))}
  </div>
);

const SipHelp = (props) => {
  const { classes } = useStyles();
  const { title } = props;

  return (
    <Paper className={classes.root} square>
      <Toolbar className={classes.toolbar}>
        <Typography className={classes.title} variant="h5" id="tableTitle" component="div">
          {title}
        </Typography>
      </Toolbar>
      <div className={classes.content}>
        <div className={classes.column}>
          <div className={classes.intro}>
            The softphone lets you make and receive calls directly in the browser when VoIP is enabled for
            your account. This page lists the call features that are available.
          </div>

          <div className={classes.section}>
            <div className={classes.sectionTitle}>Call features</div>
            <div className={classes.sectionHint}>What you can do before and during a call.</div>
            {renderFeatures(classes, callFeatures)}
          </div>

          <div className={classes.section}>
            <div className={classes.sectionTitle}>General</div>
            <div className={classes.sectionHint}>How the phone connects and starts calls.</div>
            {renderFeatures(classes, generalFeatures)}
          </div>

          <div className={classes.note}>
            <div className={classes.noteTitle}>
              <InfoOutlinedIcon fontSize="small" />
              Good to know
            </div>
            <ul className={classes.noteList}>
              <li>Calls are audio only. Video is not supported.</li>
              <li>The browser must be allowed to use your microphone, and the app must be opened over a secure (https) connection.</li>
              <li>Transfer and recording rely on the phone system's feature codes and may not be available on every system.</li>
            </ul>
          </div>
        </div>
      </div>
    </Paper>
  );
};

SipHelp.propTypes = {
  title: PropTypes.string.isRequired,
};

export default SipHelp;
